import React, { useEffect, useRef, RefObject } from 'react'
import { useEntityNotesContext } from '../context/EntityNotesContext';
import { Loader2, FileText } from 'lucide-react';

interface NotePreviewProps {
  readOnly?: boolean;
  contentElementRef?: RefObject<HTMLElement>;
}

export const NotePreview = ({ readOnly = false, contentElementRef }: NotePreviewProps) => {
  const richEditorRef = useRef<HTMLDivElement>(null);
  const { selectedNote, loading, initialize, isInitialized, setNoteData, setEditorReadOnly, cleanup, setHeight} = useEntityNotesContext();
  const { loadingNoteData } = loading;

  useEffect(() => {
    if (richEditorRef.current && !loadingNoteData) {
      initialize(richEditorRef.current);
    }
  }, [initialize, loadingNoteData]);

  useEffect(() => {
    if (isInitialized) {
      setNoteData(selectedNote.noteContent || "");
      setEditorReadOnly(readOnly);
    }
  }, [isInitialized, selectedNote.noteContent, setNoteData, setEditorReadOnly, readOnly])

  useEffect(() => {
    return () => {
      cleanup();
    }
  }, [cleanup])

  // Set CKEditor height based on container
  useEffect(() => {
    if (!contentElementRef?.current || !isInitialized || !setHeight) {
      return;
    }

    const updateHeight = () => {
      const containerHeight = contentElementRef.current?.offsetHeight || 0;
      if (containerHeight > 0) {
        setHeight(containerHeight);
      }
    };

    // Set initial height
    updateHeight();

    // Update on resize
    const resizeObserver = new ResizeObserver(updateHeight);
    if (contentElementRef.current) {
      resizeObserver.observe(contentElementRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [isInitialized, setHeight, contentElementRef]);

  if (loadingNoteData) {
    return <div className='h-full flex items-center justify-center gap-2 text-xs'>
      <Loader2 className='size-4 animate-spin' /> Loading Note...
    </div>
  }

  return (
    <>
      <div ref={richEditorRef}></div>
      {
        readOnly && !selectedNote.noteContent && !loadingNoteData && (
          <div className='absolute inset-0 h-full flex flex-col items-center justify-center gap-4'>
            <div className='flex flex-col items-center gap-3'>
              <FileText className='size-12 text-muted-foreground/50' />
              <p className='text-sm text-muted-foreground'>Empty Note</p>
            </div>
          </div>
        )
      }
    </>
  )
}